import React, {useContext, useMemo, useEffect} from 'react'
import useLearningCenter from '~/app/hooks/use-learning-center'
import {createGoalBanner} from 'hec/builder/models/learning-center'
import GoalBannerHeader from './goalBannerHeader'
import LevelCards from './levelCards'
import {BuilderContext} from '@builder.io/sdk-react'
import useMediaQuery from 'hec/hooks/useMediaQuery'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useFetchGoal from 'hec/hooks/learning-center/useFetchGoal'
import PropTypes from 'prop-types'
import {
    useCheckEnrollment,
    useCheckAvailableToEarnPoints
} from 'hec/hooks/annex-cloud/useAnnexCloud'
import useLearningCenterMutation from 'hec/hooks/learning-center/useLearningCenterMutation'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getLoyaltyEnrollment} from 'hec/helpers/getLoyaltyEnrollment'

export const GoalBanner = ({attributes}) => {
    const builderContext = useContext(BuilderContext)
    const goalRef = builderContext.content.data.goal
    const isMobile = useMediaQuery('sm')
    const {data: goalData, isLoading: isGoalDataLoading} = useFetchGoal(goalRef.id)
    const {
        learningCenterData: sfccLearningCenterData,
        getGoalByID,
        getActiveGoal
    } = useLearningCenter()
    const {setActiveGoal} = useLearningCenterMutation()
    const {data: customer, isLoading: isCustomerLoading, isRegistered} = useCurrentCustomer()
    const {site} = useMultiSite()

    const {hasData: hasAcLoyaltyData} = useMemo(
        () => getLoyaltyEnrollment(customer?.c_acLoyaltyData, site.id),
        [customer?.c_acLoyaltyData, site.id]
    )

    const {data: enrollment} = useCheckEnrollment(customer?.email, site.id, {
        enabled: hasAcLoyaltyData
    })
    const isEnrolled = enrollment?.data?.optInStatus === 'YES'

    const {data: availablePoints} = useCheckAvailableToEarnPoints(customer?.email, site.id, {
        enabled: isEnrolled
    })
    const memoizedGoalBannerData = useMemo(() => {
        if (!goalData) return null
        const [availablePointsData] = availablePoints?.data?.actionGroupDetail || []
        return {
            enrollmentData: {
                isEnrolled: isEnrolled || false,
                isMaxEarned:
                    (availablePointsData &&
                        availablePointsData?.maxLimit === availablePointsData?.earnedPoints) ||
                    null,
                loyaltyData: availablePointsData?.groupActionData || null,
                maxEarned: availablePointsData?.maxLimit || null
            },
            ...createGoalBanner(goalData.data, getGoalByID(goalData.data?.id))
        }
    }, [availablePoints, isEnrolled, sfccLearningCenterData, goalData]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isCustomerLoading) return

        const pageGoalId = goalData?.data?.id
        const active = sfccLearningCenterData?.activeGoal

        if (sfccLearningCenterData && pageGoalId && pageGoalId !== active) {
            setActiveGoal(pageGoalId)
        }
    }, [
        goalData?.data?.id,
        isCustomerLoading,
        isRegistered,
        setActiveGoal,
        sfccLearningCenterData,
        sfccLearningCenterData?.activeGoal
    ])

    if (isGoalDataLoading) return <div>Loading...</div>
    if (!memoizedGoalBannerData) return <div>Please select a goal</div>

    return (
        <div {...attributes}>
            <div
                className="w-full bg-cover"
                style={{backgroundImage: !isMobile && `url('${memoizedGoalBannerData?.image}')`}}
            >
                <div
                    className="w-full bg-cover p-8"
                    style={{backgroundImage: isMobile && `url('${memoizedGoalBannerData?.image}')`}}
                >
                    <GoalBannerHeader
                        title={memoizedGoalBannerData?.title}
                        description={memoizedGoalBannerData?.description}
                        data-testid="goalBanner-GoalBannerHeader"
                    />
                </div>
                <div className="w-full bg-cover p-8">
                    <LevelCards
                        levels={memoizedGoalBannerData?.levels}
                        enrollment={memoizedGoalBannerData.enrollmentData}
                        sfccLearningCenterData={sfccLearningCenterData}
                        data-testid="goalBanner-LevelCards"
                    />
                </div>
            </div>
        </div>
    )
}

GoalBanner.propTypes = {
    attributes: PropTypes.object
}

export const GoalBannerDefinition = {
    component: GoalBanner,
    name: 'GoalBanner',
    noWrap: true
}
